import { Skeleton } from "@quake/shared-ui/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";
import { SupportErrorState, SupportPageHeader, SupportPageShell, StatusBadge } from "./-components/support-desk-ui";
import { SupportTicketDetail } from "./-components/support-ticket-detail";

export const Route = createFileRoute("/_dashboard/$datasetSlug/support/$ticketId")({
  component: SupportTicketPage,
});

function SupportTicketPage() {
  const { datasetSlug, ticketId } = Route.useParams();
  const ticketQuery = useQuery(trpc.supportDesk.getMine.queryOptions({ ticketId }, { refetchInterval: 20_000 }));
  const ticket = ticketQuery.data;

  useBreadCrumbs([
    { title: "My support", link: `/${datasetSlug}/support` },
    { title: ticket?.ticketId ?? ticketId, link: `/${datasetSlug}/support/${ticketId}` },
  ]);

  return (
    <SupportPageShell>
      <SupportPageHeader
        title={ticket?.subject ?? "Support ticket"}
        description={ticket?.ticketId ?? ticketId}
        datasetSlug={datasetSlug}
        back
        action={ticket ? <StatusBadge status={ticket.status} /> : null}
      />

      {ticketQuery.isPending ? <TicketDetailSkeleton /> : null}
      {ticketQuery.isError ? (
        <SupportErrorState message={ticketQuery.error.message} onRetry={() => void ticketQuery.refetch()} />
      ) : null}
      {ticket ? <SupportTicketDetail ticket={ticket} /> : null}
    </SupportPageShell>
  );
}

function TicketDetailSkeleton() {
  return (
    <div className="grid gap-5 py-5 lg:grid-cols-[minmax(0,1fr)_17rem]" aria-label="Loading support ticket">
      <div className="space-y-5">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex gap-3">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        ))}
        <Skeleton className="h-44 w-full" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}
